/**
 * Transform measurements into BlueprintJS Tree format.
 * Each node with children gets isExpanded: false by default.
 */
import React from 'react';
import styled from 'styled-components';
import { Button, Flex, Icon, Text } from 'core/components';
import {
  DirectionalMeasurementDimension,
  Measurement,
  MeasurementDimension,
  MeasurementMetric
} from '@kentik/ui-shared/query/query.types';
import { isDstDim, isSrcDim } from '@kentik/ui-shared/query/query';
import { checkMeasurement } from '@kentik/ui-shared/query/measurements';
import { getDimensionIcon, getMetricIcon } from 'app/views/boards/panels/ude/selectorIcons';
import {
  fuzzyMatchAllTokens,
  isDirectionalDim,
  isSupportedMeasurement,
  UdeTreeNode
} from 'app/views/boards/panels/ude/treeUtils';

// converts the measurement path to a more user-friendly label
export const titleExactMatches: Record<string, string> = {
  'host-resources': 'Host Resources',
  'palo-alto': 'Palo Alto',
  accedian: 'Accedian',
  apc: 'APC',
  agent: 'Agent',
  amazon: 'Amazon',
  'amazon-aws': 'Amazon AWS',
  applicationGateways: 'Application Gateways',
  aws_subnet: 'AWS',
  loadBalancers: 'Load Balancers',
  ApiGateway: 'API Gateway',
  NATGateway: 'NAT Gateway',
  PrivateLinkEndpoints: 'Private Link Endpoints',
  'Transit Gateway': 'Transit Gateway',
  azure: 'Azure',
  azure_subnet: 'Azure',
  bgp: 'BGP',
  cdp: 'CDP',
  cisco: 'Cisco',
  cloud: 'Cloud',
  'container-networking': 'Container',
  common: 'Common',
  components: 'Components',
  cost: 'Cost Analysis',
  cpus: 'CPUs',
  devices: 'Devices',
  dns: 'DNS',
  emerson: 'Emerson',
  events: 'Events',
  force10: 'Force10',
  fortinet: 'Fortinet',
  f5: 'F5',
  flow: 'Flow',
  gcp_subnet: 'GCP',
  interfaces: 'Interfaces',
  isis: 'ISIS',
  lldp: 'LLDP',
  oci_subnet: 'OCI',
  ospf: 'OSPF',
  synthetics: 'Synthetics',
  http: 'HTTP',
  pageload: 'Page Load',
  throughput: 'Throughput',
  ping: 'Ping',
  system: 'System',
  traffic: 'Traffic',
  status: 'Status',
  syslog: 'Syslog',
  vertiv: 'Vertiv',
  vendors: 'Vendors',
  metrics: 'NMS',
  counters: 'Counters',
  kentik: 'Kentik',
  protocols: 'Protocols',
  utilization: 'Utilization',
  arista: 'Arista',
  juniper: 'Juniper',
  paloalto: 'Palo Alto',
  nms: 'NMS',
  event_syslog: 'Syslog',
  event_snmp_traps: 'SNMP Traps',
  event_deviceconf: 'Device Config Change'
} as const;

const DirectionButton = styled(Button)`
  &.bp4-button {
    font-weight: 500;
    height: 20px;
    min-height: auto;
    font-size: 10px;
    box-shadow: none;
  }

  &.select-directionality-active {
    opacity: 0.3;
  }
`;

export const DirectionalButtons = <T,>(props: {
  baseNode: UdeTreeNode;
  directions: DirectionalMeasurementDimension['Directions'];
  onNodeSelect: (selection: T) => void;
  onRemove?: (key: string) => void;
  matches?: { name: string }[];
}) => {
  const { baseNode, directions, onNodeSelect, matches = [], onRemove } = props;

  return (
    <Flex alignItems="center" gap={1} flex={1}>
      <Text textAlign="left" small flex={1}>
        {baseNode.nodeData?.Label}
      </Text>
      <Flex alignItems="center" gap="2px" flexShrink={0}>
        {Object.entries(directions).map(([dirKey, contents]) => {
          const buttonText = dirKey === 'dst' ? 'DEST' : 'SRC';
          const isActive = matches.some((m) => m.name === contents.key);
          return (
            <DirectionButton
              key={contents.key}
              data-dimension={contents.key}
              onClick={(e) => {
                e.stopPropagation();

                if (isActive && onRemove) {
                  onRemove(contents?.key ?? contents.Column);

                  return;
                }

                const selection = {
                  ...baseNode,
                  ...contents,
                  id: `${baseNode.measurement}/dimension/${contents.key}`,
                  value: contents.key ?? contents.Column
                } as T;

                if (onNodeSelect) {
                  onNodeSelect(selection);
                }
              }}
              className={isActive ? 'select-directionality select-directionality-active' : 'select-directionality'}
              small
              text={buttonText}
            />
          );
        })}
      </Flex>
    </Flex>
  );
};

function isDirectionalMeasurementDimension(
  dim: MeasurementDimension | DirectionalMeasurementDimension
): dim is DirectionalMeasurementDimension {
  return typeof dim === 'object' && dim !== null && 'Directions' in dim && typeof dim.Directions === 'object';
}

function getGroupingKey(key: string, value: MeasurementDimension): string {
  if (value.Inverse) {
    return [key, value.Inverse].sort().join('|');
  }

  if (key.endsWith('_in') || key.endsWith('_out')) {
    return key.replace(/_in$|_out$/, '');
  }

  return key.replace(/_?(src|dst|input|output)_?/, '');
}

export function transformMeasurementsToBlueprintTree<T>(
  measurements: Measurement[] = [],
  onNodeSelect: (selection: T) => void,
  onNodeRemove?: (key: string) => void
): UdeTreeNode[] {
  function getOrCreateNode(map: Record<string, UdeTreeNode>, id: string, label: string, depth: number) {
    if (!map[id]) {
      map[id] = { id, label, childNodes: [], depth, disabled: !isSupportedMeasurement(id) };
    }
    return map[id];
  }

  const rootMap: Record<string, UdeTreeNode> = {};

  // Find /traffic measurement once to avoid repeated lookups in the loop
  const trafficMeasurement = measurements.find((m) => m.measurement === '/traffic');
  const trafficSharedMetrics = trafficMeasurement?.storage?.Metrics
    ? Object.entries(trafficMeasurement.storage.Metrics).reduce(
        (acc, [key, value]) => {
          if (value.Shared) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, MeasurementMetric>
      )
    : {};
  const trafficSharedDimensions = trafficMeasurement?.storage?.Dimensions
    ? Object.entries(trafficMeasurement.storage.Dimensions).reduce(
        (acc, [key, value]) => {
          if (value.Shared) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, MeasurementDimension>
      )
    : {};

  measurements.forEach((measurementObj) => {
    const path = measurementObj.measurement;
    if (!path) {
      return;
    }
    const segments = path.split('/').filter(Boolean);
    let currentMap = rootMap;
    let parent: null | UdeTreeNode = null;
    let nodeIdPath = '';

    segments.forEach((seg, i) => {
      nodeIdPath += `/${seg}`;

      let label = titleExactMatches[seg] || seg;
      if (i === segments.length - 1) {
        // Last segment is the measurement itself
        label = measurementObj.displayName || label;
      }

      const node = getOrCreateNode(currentMap, nodeIdPath, label, i);
      // If this is the last segment (the measurement itself), always update the label
      // with the displayName so that nodes created earlier as intermediate segments
      // get the correct display label when their measurement is processed later.
      if (i === segments.length - 1 && measurementObj.displayName) {
        node.label = measurementObj.displayName;
      }
      if (parent && Array.isArray(parent.childNodes)) {
        if (!parent.childNodes.some((child) => child.id === node.id)) {
          parent.childNodes.push(node);
        }
      }
      parent = node;
      if (!node._childMap) {
        node._childMap = {};
      }
      currentMap = node._childMap;
    });

    // Add Metrics and Dimensions as children
    const leaf: UdeTreeNode = parent ?? {
      id: path,
      label: path,
      childNodes: [],
      depth: segments.length,
      disabled: false
    };
    let { Metrics = {}, Dimensions = {} } = measurementObj.storage || {};

    // For traffic root, only show shared items (not child-specific items)
    if (checkMeasurement.isTrafficRoot(path)) {
      Metrics = trafficSharedMetrics;
      Dimensions = trafficSharedDimensions;
    }
    // For all traffic children (both intermediate and leaf), only show unique items
    else if (checkMeasurement.isTrafficChild(path)) {
      const childDimensions: Record<string, MeasurementDimension> = {};

      for (const [key, value] of Object.entries(Dimensions)) {
        if (!value.Shared) {
          childDimensions[key] = value;
        }
      }

      Metrics = { ...trafficSharedMetrics, ...Metrics };
      Dimensions = childDimensions;
    }

    const leafDepth = segments.length;

    Object.entries(Metrics).forEach(([key, value]) => {
      if (Array.isArray(leaf.childNodes)) {
        leaf.childNodes.push({
          id: `${path}/metric/${key}`,
          className: 'metric',
          parentMeasurement: leaf.measurement,
          measurement: measurementObj.measurement,
          type: 'metric',
          value: key,
          label: (
            <Flex alignItems="center" gap={1}>
              <Icon icon={getMetricIcon(value)} size={12} color="muted" />
              {value.Label || key}
            </Flex>
          ),
          // include the dimensions with the metric so we can do a Union between the select metrics and figure
          // out which dimensions are available
          nodeData: { ...value, type: 'metric', measurement: path, dimensions: Dimensions },
          depth: leafDepth
        });
      }
    });

    const groupedDirectionalDimensions = Object.entries(Dimensions).reduce<
      Record<string, MeasurementDimension | DirectionalMeasurementDimension>
    >(
      (acc, [key, value]) => {
        if (value.Direction || isDirectionalDim(key)) {
          let direction;
          if (value.Direction === 'src' || isSrcDim(key)) {
            direction = 'src';
          }
          if (value.Direction === 'dst' || isDstDim(key)) {
            direction = 'dst';
          }

          if (!direction) {
            // Skip dimensions where direction can't be determined rather than crashing the tree
            acc[key] = value;
            return acc;
          }
          const groupingKey = getGroupingKey(key, value);
          if (groupingKey in acc && isDirectionalMeasurementDimension(acc[groupingKey])) {
            const existing = acc[groupingKey] as DirectionalMeasurementDimension;
            // Merge SRC/DST into one entry
            acc[groupingKey] = {
              ...existing,
              Directions: { ...(existing.Directions || {}), [direction]: { ...value, key } }
            } as DirectionalMeasurementDimension;
            return acc;
          }

          const sanitizedLabel = value?.Label?.replaceAll('Source', '').replaceAll('Destination', '').trim();
          const { Column: _Column, ...rest } = value;

          const directions: DirectionalMeasurementDimension['Directions'] = {
            [direction]: { ...value, key }
          };

          return {
            ...acc,
            [groupingKey]: {
              ...rest,
              Label: sanitizedLabel,
              Directions: directions
            }
          };
        }

        acc[key] = value;
        return acc;
      },
      Object.create(null) as Record<string, MeasurementDimension | DirectionalMeasurementDimension>
    );

    Object.entries(groupedDirectionalDimensions).forEach(([dimKey, directionalDim]) => {
      let baseNode: UdeTreeNode = {
        id: `${path}/dimension/${dimKey}`,
        parentMeasurement: leaf.measurement,
        measurement: measurementObj.measurement,
        className: 'dimension',
        type: 'dimension' as const,
        label: (
          <Flex alignItems="center" gap={1}>
            <Icon icon={getDimensionIcon(directionalDim, dimKey, directionalDim?.Category)} size={12} color="muted" />
            {directionalDim?.Label || dimKey}
          </Flex>
        ),
        value: dimKey,
        nodeData: { ...directionalDim, type: 'dimension', measurement: path, metrics: Metrics },
        depth: leafDepth
      };

      if (Array.isArray(leaf.childNodes)) {
        if (isDirectionalMeasurementDimension(directionalDim)) {
          baseNode = {
            ...baseNode,
            isDirectionalNode: true, // Make the parent node non-clickable without disabled styling
            label: (
              <Flex alignItems="center" gap={1} flex={1}>
                <Icon
                  icon={getDimensionIcon(directionalDim, dimKey, directionalDim?.Category)}
                  size={12}
                  color="muted"
                />
                <DirectionalButtons
                  baseNode={baseNode}
                  key={dimKey}
                  directions={directionalDim?.Directions}
                  onNodeSelect={onNodeSelect}
                  onRemove={onNodeRemove}
                />
              </Flex>
            )
          };
        }

        leaf.childNodes.push(baseNode);
      }
    });
  });

  // Recursively remove _childMap, add isExpanded, and set correct depth
  function clean(node: UdeTreeNode, depth = 0) {
    if (node._childMap) {
      delete node._childMap;
    }
    node.depth = depth;
    if (node.childNodes && node.childNodes.length > 0) {
      // Sort child nodes: those with nodeData.Popular=true first, then Dimensions, then Metrics, then alphabetically by label
      node.childNodes.sort((a, b) => {
        const isPopularA = !!(a.nodeData && a.nodeData.Popular);
        const isPopularB = !!(b.nodeData && b.nodeData.Popular);
        if (isPopularA && !isPopularB) {
          return -1;
        }
        if (!isPopularA && isPopularB) {
          return 1;
        }
        // Dimensions first, then Metrics
        const typeA = a.type || (a.nodeData && a.nodeData.type);
        const typeB = b.type || (b.nodeData && b.nodeData.type);
        if (typeA === 'dimension' && typeB !== 'dimension') {
          return -1;
        }
        if (typeA !== 'dimension' && typeB === 'dimension') {
          return 1;
        }
        if (typeA === 'metric' && typeB !== 'metric') {
          return 1;
        }
        if (typeA !== 'metric' && typeB === 'metric') {
          return -1;
        }
        // Alphabetical by label
        const labelA = (a?.nodeData?.Label || '').toLowerCase();
        const labelB = (b?.nodeData?.Label || '').toLowerCase();
        if (labelA < labelB) {
          return -1;
        }
        if (labelA > labelB) {
          return 1;
        }
        return 0;
      });
      node.isExpanded = false;
      node.childNodes = node.childNodes.map((child) => clean(child, depth + 1));
    }
    return node;
  }

  return Object.values(rootMap).map((node) => clean(node, 0));
}

/**
 * Recursively filter BlueprintJS tree nodes by label or any value in nodeData.
 * Returns a new filtered tree (preserving hierarchy) or [] if no match.
 * @param {Array} nodes - The tree contents array
 * @param {string} query - The search string
 * @param {object | null} allowedDimensionsObject - The allowed dimensions object
 * @returns {Array} filtered tree
 */
export function filterTreeNodes(
  nodes: UdeTreeNode[],
  query: string,
  allowedDimensionsObject?: Record<string, unknown> | null
): UdeTreeNode[] {
  const tokens = query ? query.toLowerCase().split(/\s+/).filter(Boolean) : [];

  function nodeMatches(node: UdeTreeNode, ancestorContext = '') {
    // Build searchable text from node fields + ancestor path context
    // This lets "pdu state" match a "state" leaf under a "/metrics/.../pdu/..." parent
    const idStr = typeof node.id === 'string' ? node.id : '';
    const parts = [ancestorContext, node.label ? String(node.label) : '', node.value ? String(node.value) : '', idStr];
    if (node.nodeData) {
      Object.values(node.nodeData).forEach((val) => {
        if (val) {
          parts.push(String(val));
        }
      });
    }
    const haystack = parts.join(' ').toLowerCase();
    const matchesQuery = tokens.length > 0 && fuzzyMatchAllTokens(tokens, haystack);

    // If there are filtered dimensions, check if this dimension node is allowed
    if (node.value && node.type === 'dimension' && allowedDimensionsObject) {
      return matchesQuery && typeof allowedDimensionsObject === 'object' && allowedDimensionsObject[node.value];
    }

    // For other node types (metrics, groups), just check if they match the query
    return matchesQuery;
  }

  function filterNode(node: UdeTreeNode, ancestorContext = ''): UdeTreeNode | null {
    // Build context string for this node's children (accumulates parent labels/ids)
    const nodeLabel = node.label ? String(node.label) : '';
    const nodeId = typeof node.id === 'string' ? node.id : '';
    const childContext = [ancestorContext, nodeLabel, nodeId].filter(Boolean).join(' ');

    let matched = nodeMatches(node, ancestorContext);
    let filteredChildren: UdeTreeNode[] = [];
    if (node.childNodes && node.childNodes.length > 0) {
      // Recursively filter children, passing the ancestorContext
      filteredChildren = node.childNodes
        .map((child: UdeTreeNode) => filterNode(child, childContext))
        .filter((n): n is UdeTreeNode => n !== null);
      if (filteredChildren.length > 0) {
        // Sort child nodes: those with nodeData.Popular=true first, then alphabetically by label
        filteredChildren.sort((a, b) => {
          const isPopularA = !!(a.nodeData && a.nodeData.Popular);
          const isPopularB = !!(b.nodeData && b.nodeData.Popular);
          if (isPopularA && !isPopularB) {
            return -1;
          }
          if (!isPopularA && isPopularB) {
            return 1;
          }
          const labelA = (a?.nodeData?.Label || '').toLowerCase();
          const labelB = (b?.nodeData?.Label || '').toLowerCase();
          if (labelA < labelB) {
            return -1;
          }
          if (labelA > labelB) {
            return 1;
          }
          return 0;
        });
        // If any children match, the parent group/metric should also be included
        matched = true;
      }
    }

    // Include the node if it matches the query/dimensions or has matching children
    if (matched) {
      // Return a new object, do not mutate the original
      return {
        ...node,
        // isExpanded state will be handled separately
        childNodes: filteredChildren.length ? filteredChildren : undefined
      };
    }
    return null;
  }

  // Filter the top-level nodes
  return nodes.map((n) => filterNode(n, '')).filter((n): n is UdeTreeNode => n !== null);
}

// Function to merge expanded state from one tree to another
export function mergeExpandedState(currentTree: UdeTreeNode[], newTree: UdeTreeNode[]): UdeTreeNode[] {
  const expandedNodes = new Set();

  // Collect IDs of expanded nodes from the current tree
  function collectExpanded(nodes: UdeTreeNode[]) {
    nodes.forEach((node) => {
      if (node.isExpanded) {
        expandedNodes.add(node.id);
      }
      if (node.childNodes) {
        collectExpanded(node.childNodes);
      }
    });
  }

  collectExpanded(currentTree);

  // Apply expanded state to the new tree and ensure parents of expanded nodes are also expanded
  function applyExpanded(nodes: UdeTreeNode[]): UdeTreeNode[] {
    return nodes.map((node) => {
      const newNode = { ...node };
      let shouldExpand = expandedNodes.has(newNode.id);

      if (newNode.childNodes) {
        newNode.childNodes = applyExpanded(newNode.childNodes);
        // If any child is expanded, the parent should also be expanded
        if (newNode.childNodes.some((child) => child.isExpanded)) {
          shouldExpand = true;
        }
      }

      newNode.isExpanded = shouldExpand;
      return newNode;
    });
  }

  return applyExpanded(newTree);
}
